import React, { useEffect, useMemo, useState } from "react";
import { Loader2, X, Package2, Truck, MapPin, User } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Iteration 143 — Order Summary Modal.
 *
 * Permanent buyer/dispatch summary drawer for the Parent "Ledger &
 * Activity → Orders" tab. Displays buyer's full name and delivery
 * address (permanently accessible even after the sale is complete),
 * lets the seller-parent pick a carrier (Royal Mail / Evri) and enter
 * an optional tracking number, and submits `POST
 * /api/orders/{id}/mark-shipped` when the order is still "Awaiting
 * Shipping".
 *
 * Props:
 *   - orderId: string (required)
 *   - open:    boolean — whether the drawer is visible
 *   - onClose: () => void
 *   - onShipped: (updatedSummary) => void — called after a successful
 *     mark-as-shipped so the parent list can refresh the pill/CTA.
 */
export default function OrderSummaryModal({ orderId, open, onClose, onShipped }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [carrier, setCarrier] = useState("");
  const [tracking, setTracking] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !orderId) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const { data } = await api.get(`/orders/${orderId}/summary`);
        if (cancel) return;
        setSummary(data || null);
        setCarrier(data?.carrier || "");
        setTracking(data?.tracking_number || "");
      } catch (e) {
        if (!cancel) setErr(formatApiError(e) || "Couldn't load the order summary.");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [open, orderId]);

  const isAwaiting = useMemo(() => (summary?.status || "").toLowerCase() === "paid", [summary]);

  const submit = async () => {
    if (!orderId) return;
    const carrierValue = carrier.trim();
    const trackingValue = tracking.trim();
    if (!carrierValue || !trackingValue) {
      setErr("Carrier name and tracking/reference number are required before marking this order as posted.");
      return;
    }
    setSubmitting(true);
    setErr("");
    try {
      await api.post(`/orders/${orderId}/mark-shipped`, {
        carrier: carrierValue,
        tracking_number: trackingValue,
      });
      // Re-fetch the freshly-updated summary and bubble it up so the
      // list refreshes the pill + CTA without a full page reload.
      const { data } = await api.get(`/orders/${orderId}/summary`);
      setSummary(data || null);
      if (typeof onShipped === "function") onShipped(data || {});
    } catch (e) {
      setErr(formatApiError(e) || "Couldn't mark the order as shipped.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const addr = summary?.delivery_address || {};
  const buyerName = summary?.recipient_name || summary?.buyer_name || "Buyer";
  const addrLine = [addr.line1 || addr.street, addr.line2, addr.city, addr.postcode]
    .filter(Boolean)
    .join(", ");

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="order-summary-modal"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl p-6 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-2xl bg-slate-100 text-slate-700 inline-flex items-center justify-center">
              <Package2 className="w-4 h-4" />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Order Summary</div>
              <div className="text-sm font-semibold text-slate-900">
                {isAwaiting ? "Awaiting Shipping" : (summary?.status === "shipped" ? "Shipped" : (summary?.status || "—"))}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900 rounded-full p-1"
            data-testid="order-summary-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="py-10 flex items-center justify-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : err ? (
          <div className="rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-sm px-3 py-2" data-testid="order-summary-error">
            {err}
          </div>
        ) : (
          <div className="space-y-4">
            <section>
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-1">
                <User className="w-3.5 h-3.5" /> Buyer
              </div>
              <div className="text-sm font-semibold text-slate-900" data-testid="order-summary-buyer-name">{buyerName}</div>
            </section>

            <section>
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-1">
                <MapPin className="w-3.5 h-3.5" /> Delivery Address
              </div>
              <div className="text-sm text-slate-800 whitespace-pre-line" data-testid="order-summary-address">
                {addrLine || "No address on file."}
              </div>
            </section>

            <section>
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-1">
                <Truck className="w-3.5 h-3.5" /> Carrier <span className="text-rose-500">*</span>
              </div>
              <div className="flex gap-2" data-testid="order-summary-carrier-group">
                {[
                  { key: "royal_mail", label: "Royal Mail" },
                  { key: "evri", label: "Evri" },
                ].map((c) => {
                  const active = carrier === c.key;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setCarrier(c.key)}
                      disabled={!isAwaiting && !!summary?.carrier}
                      className={`px-3 py-2 rounded-xl text-xs font-bold border transition ${
                        active
                          ? "bg-brandSlate text-white border-brandSlate"
                          : "bg-white text-slate-700 border-slate-200 hover:border-brandSlate"
                      } ${!isAwaiting && !!summary?.carrier ? "opacity-70 cursor-not-allowed" : ""}`}
                      data-testid={`order-summary-carrier-${c.key}`}
                      aria-pressed={active}
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </section>

            <section>
              <label htmlFor="order-summary-tracking" className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block mb-1">
                Tracking/Reference Number <span className="text-rose-500">*</span>
              </label>
              <input
                id="order-summary-tracking"
                type="text"
                value={tracking}
                onChange={(e) => setTracking(e.target.value)}
                placeholder="e.g. AB123456789GB"
                required
                aria-required="true"
                disabled={!isAwaiting && !!summary?.tracking_number}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-brandSlate disabled:opacity-70"
                data-testid="order-summary-tracking-input"
              />
            </section>

            {isAwaiting && (
              <button
                type="button"
                onClick={submit}
                disabled={submitting || !carrier.trim() || !tracking.trim()}
                className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm px-4 py-3 disabled:opacity-70 disabled:cursor-not-allowed"
                data-testid="order-summary-mark-shipped"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
                Mark as Shipped
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
